import React, { useMemo, useCallback } from "react";
import { Container, Text } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { formatToCustomLocale } from "../../../../utilities";
import { PixiSettings } from "../../../../pixiSettings";
import { FONT_CONFIG, TEXT_STYLE_CONFIG } from "../../../../pixiSettings";
import {
  selectBet,
  selectBets,
  selectCurrency,
  selectBetSelectPopupActive,
  selectCriticalAnimationPlaying,
  selectHoldAndWinActive,
  selectIsSpinning,
  selectAutoPlayActive,
  selectFreeRoundsActive,
  selectFreeSpinActive,
} from "../../../../State/selectors";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  openBetSelectPopup,
  closeBetSelectPopup,
} from "../../../../State/actions";
import ButtonDecrementBet from "../Buttons/ButtonDecrementBet";
import ButtonIncrementBet from "../Buttons/ButtonIncrementBet";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import { FEATURES_CONFIG } from "../../../../featuresConfig";

const BET_CONTROL_BUTTON_CONFIG = {
  BASE_WIDTH: 45,
  BASE_HEIGHT: 45,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.4,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.2,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.2,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.2,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

const BetDisplay = ({ toolbar, position }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const bet = useSelector(selectBet);
  const bets = useSelector(selectBets);
  const currency = useSelector(selectCurrency);
  const popupActive = useSelector(selectBetSelectPopupActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const isSpinning = useSelector(selectIsSpinning);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  const { device, orientation, getResponsiveConfig, uiSymbolScale } =
    useDimensions();

  // Get button dimensions using the same system as PopiBalanceDisplay - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(BET_CONTROL_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Get unified bet configuration (font multiplier, y position) - memoized
  // X position now comes from toolbar.layout
  const betConfig = useMemo(() => {
    let fontMultiplier = 1.0;
    let y = 0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.3;
        y = toolbar.height / 2;
      } else {
        fontMultiplier = 1.1;
        y = toolbar.height / 2;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.5;
        y = toolbar.height / 2;
      } else {
        fontMultiplier = 1.25;
        y = toolbar.height / 2;
      }
    } else {
      // Desktop
      fontMultiplier = 1.0;
      y = toolbar.height / 2;
    }

    return { fontMultiplier, y };
  }, [device, orientation, toolbar.height]);

  // Calculate font sizes - memoized
  const baseFontSize = 32;
  const FONT_SIZE = useMemo(() => {
    const responsiveFontSize = baseFontSize * betConfig.fontMultiplier;
    return Math.round(responsiveFontSize * uiSymbolScale);
  }, [betConfig.fontMultiplier, uiSymbolScale]);

  const LABEL_FONT_SIZE = useMemo(
    () => Math.round(FONT_SIZE * 0.8), // Slightly smaller for label
    [FONT_SIZE],
  );

  // Text style for bet label - memoized to prevent creating new TextStyle on every render
  const betLabelStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: LABEL_FONT_SIZE,
        fontWeight: "600",
        fill: "#ffbf11", // Accent yellow same as WinDisplay labels
        align: "left",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [LABEL_FONT_SIZE],
  );

  // Format bet and prepare text content - memoized
  const formattedBet = useMemo(() => formatToCustomLocale(bet), [bet]);
  const betLabelText = useMemo(() => t("bet").toUpperCase(), [t]);
  const betValueText = useMemo(() => formattedBet, [formattedBet]);
  const betCurrencyText = useMemo(() => ` ${currency}`, [currency]);
  // Combined text for width calculations only
  const betAmountText = useMemo(
    () => `${formattedBet} ${currency}`,
    [formattedBet, currency],
  );

  // Calculate max bet width based on largest bet in bets array (keeps buttons fixed)
  const maxBetWidth = useMemo(() => {
    if (!bets || bets.length === 0) {
      // Fallback: measure current bet if bets array not available
      const tempStyle = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: FONT_SIZE,
        fontWeight: "700",
      });
      return TextMetrics.measureText(betAmountText, tempStyle).width;
    }

    const maxBet = Math.max(...bets);
    const maxBetText = `${formatToCustomLocale(maxBet)} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: FONT_SIZE,
      fontWeight: "700",
    });
    const maxBetMetrics = TextMetrics.measureText(maxBetText, tempStyle);

    return maxBetMetrics.width;
  }, [bets, currency, FONT_SIZE, betAmountText]);

  // Auto-scale font size if actual bet text exceeds max width (matches BalanceDisplay)
  const finalBetFontSize = useMemo(() => {
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: FONT_SIZE,
      fontWeight: "700",
    });
    const metrics = TextMetrics.measureText(betAmountText, tempStyle);

    // If text exceeds max width, scale down proportionally
    if (metrics.width > maxBetWidth) {
      const scale = maxBetWidth / metrics.width;
      return Math.round(FONT_SIZE * scale);
    }

    // Return ideal font size if it fits
    return FONT_SIZE;
  }, [betAmountText, FONT_SIZE, maxBetWidth]);

  const betAmountStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalBetFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "left",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [finalBetFontSize],
  );

  // Currency style — 0.7x of the amount font size
  const betCurrencyStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: Math.floor(finalBetFontSize * 0.7),
        fontWeight: "700",
        fill: 0xffffff,
        align: "left",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [finalBetFontSize],
  );


  // Measure text dimensions for positioning - memoized
  const textMetrics = useMemo(() => {
    const labelMetrics = TextMetrics.measureText(betLabelText, betLabelStyle);
    const valueMetrics = TextMetrics.measureText(betValueText, betAmountStyle);
    const currencyMetrics = TextMetrics.measureText(
      betCurrencyText,
      betCurrencyStyle,
    );
    const amountLineWidth = valueMetrics.width + currencyMetrics.width;

    const totalHeight = labelMetrics.height + valueMetrics.height;
    const maxWidth = Math.max(labelMetrics.width, maxBetWidth);

    const labelY = Math.round(-totalHeight / 2 + labelMetrics.height / 2);
    const amountY = Math.round(totalHeight / 2 - valueMetrics.height / 2);

    return {
      labelY,
      amountY,
      maxWidth,
      valueMetrics,
      currencyMetrics,
      amountLineWidth,
      // Bottom-align: shift smaller currency down by half the height difference
      currencyYOffset: Math.round(
        (valueMetrics.height - currencyMetrics.height) * 0.3,
      ),
    };
  }, [
    betLabelText,
    betValueText,
    betCurrencyText,
    betLabelStyle,
    betAmountStyle,
    betCurrencyStyle,
    maxBetWidth,
  ]);

  // Button positioning - memoized
  const buttonPositions = useMemo(() => {
    const gap = buttonConfig.width * 0.075;

    // Calculate spacing: button + gap + text + gap + button
    const totalControlsWidth =
      buttonConfig.width +
      gap +
      textMetrics.maxWidth +
      gap +
      buttonConfig.width;

    // Position everything symmetrically around center
    const decrementX = -totalControlsWidth / 2;
    const textCenterX = Math.round(
      -totalControlsWidth / 2 +
        buttonConfig.width +
        gap +
        textMetrics.maxWidth / 2,
    );
    const incrementX = totalControlsWidth / 2 - buttonConfig.width;

    return { decrementX, textCenterX, incrementX };
  }, [buttonConfig.width, textMetrics.maxWidth]);

  // Container positioning - memoized
  const containerPosition = useMemo(() => {
    // Use layout position from toolbar hook if available, with fallback to old logic
    const fallbackX =
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? toolbar.width - buttonConfig.width * 1.5
        : device === PixiSettings.DEVICE.TABLET &&
            orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? toolbar.width - buttonConfig.width * 2
          : buttonConfig.width * 8.5;

    const containerX = position?.x ?? toolbar.layout?.bet?.x ?? fallbackX;
    const containerY = position?.y ?? betConfig.y;
    const buttonsY = 0;

    return { containerX, containerY, buttonsY };
  }, [
    position?.x,
    position?.y,
    toolbar.layout?.bet?.x,
    toolbar.width,
    device,
    orientation,
    buttonConfig.width,
    betConfig.y,
  ]);

  // Always show +/- buttons on all devices in the new UI
  const showButtons = true;

  // Calculate if bet selection should be disabled
  const isDisabled =
    criticalAnimationPlaying ||
    holdAndWinActive ||
    isSpinning ||
    autoPlayActive ||
    freeSpinActive ||
    (FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive);

  // Handle bet display click - toggles bet select popup
  const handleBetDisplayClick = useCallback(() => {
    // Don't allow bet selection during critical states
    if (isDisabled) {
      return;
    }

    soundManager.play(SOUND_NAME.button_click);
    if (popupActive) {
      dispatch(closeBetSelectPopup());
    } else {
      dispatch(openBetSelectPopup());
    }
  }, [isDisabled, popupActive, dispatch]);

  return (
    <Container
      name="secondary_bet_display"
      x={containerPosition.containerX}
      y={containerPosition.containerY}
      zIndex={5}
      accessible={true}
      accessibleTitle={`${t("bet", { ns: "common" })}: ${formattedBet} ${currency}`}
    >
      {/* Clickable text container */}
      <Container
        name="bet_text_container"
        eventMode="static"
        cursor="pointer"
        onpointertap={handleBetDisplayClick}
      >
        <Text
          text={betLabelText}
          style={betLabelStyle}
          anchor={{ x: 0.5, y: 0.5 }}
          x={buttonPositions.textCenterX}
          y={textMetrics.labelY}
          resolution={2}
        />
        <Container y={textMetrics.amountY}>
          <Text
            text={betValueText}
            style={betAmountStyle}
            anchor={{ x: 0.5, y: 0.5 }}
            x={Math.round(
              buttonPositions.textCenterX -
                textMetrics.amountLineWidth / 2 +
                textMetrics.valueMetrics.width / 2,
            )}
            resolution={2}
          />
          <Text
            text={betCurrencyText}
            style={betCurrencyStyle}
            anchor={{ x: 0.5, y: 0.5 }}
            x={Math.round(
              buttonPositions.textCenterX +
                textMetrics.amountLineWidth / 2 -
                textMetrics.currencyMetrics.width / 2,
            )}
            y={textMetrics.currencyYOffset}
            resolution={2}
          />
        </Container>
      </Container>

      {showButtons && (
        <ButtonDecrementBet
          toolbar={toolbar}
          x={buttonPositions.decrementX}
          y={containerPosition.buttonsY}
        />
      )}
      {showButtons && (
        <ButtonIncrementBet
          toolbar={toolbar}
          x={buttonPositions.incrementX}
          y={containerPosition.buttonsY}
        />
      )}
    </Container>
  );
};

export default BetDisplay;
